import React, { useEffect, useState } from 'react';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountSettingsService, type AccountSettings, type FeatureMode } from '@/services/account-settings-service';
import type { AppSettings } from '@/types/api';
import { accountService } from '@/services/account-service';
import { logoUploadService } from '@/services/logo-upload-service';
import { usePageContext } from '@/contexts/page-context';
import { 
  Header, 
  Tabs, 
  TabsList, 
  TabsTrigger, 
  TabsContent, 
  ContentContainer, 
  toast,
  Input,
  Field,
  FieldLabel,
  FieldError,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Switch,
  CardFooter,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  FullPageLoader,
  Spinner,
  Label,
  Progress,
  RadioGroup,
  RadioGroupItem
} from 'prosci-ui';
import { 
  Upload,
  Copy,
  MoreHorizontal,
  Trash2,
  CreditCard,
  AlertTriangle,
  Users,
  Lightbulb,
  Settings
} from '@/lib/icons';

export function SettingsPage() {
  const { data: currentUser, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { setShowPageHeader } = usePageContext();
  const [activeTab, setActiveTab] = useState('account');
  
  // Check if user is a license admin
  const isLicenseAdmin = currentUser?.type === 'license_admin';
  const isProsciAdmin = currentUser?.type === 'prosci_admin';
  
  // Hide the page header
  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);
  
  // License admins only see this page, Prosci admins should not
  useEffect(() => {
    if (!isLoading && isProsciAdmin) {
      // Redirect Prosci admins away from this page
      navigate('/users');
    }
  }, [isProsciAdmin, isLoading, navigate]);
  
  // Show loading while checking user permissions
  if (isLoading) {
    return <FullPageLoader message="Loading..." />;
  }
  
  // Only show content for license admins
  if (!isLicenseAdmin) {
    return null;
  }
  
  return (
    <div>
      <Header 
        title="Account Settings" 
        size="small"
      />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="bg-white border-b border-accent-sand">
          <div className="max-w-[1160px] mx-auto px-6">
            <TabsList className="border-0">
              <TabsTrigger value="account">Account Details</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="billing">Billing and Usage</TabsTrigger>
              {isLicenseAdmin && (
                <TabsTrigger value="app-level">App Level Settings</TabsTrigger>
              )}
            </TabsList>
          </div>
        </div>
        <ContentContainer>
          <TabsContent value="account">
            <AccountDetailsTab />
          </TabsContent>
          
          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>
          
          <TabsContent value="billing">
            <BillingUsageTab />
          </TabsContent>

          {isLicenseAdmin && (
            <TabsContent value="app-level">
              <AppLevelSettingsTab />
            </TabsContent>
          )}
        </ContentContainer>
      </Tabs>
    </div>
  );
}

function AccountDetailsTab() {
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();
  const accountId = currentUser?.organization_id || '';
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const [companyName, setCompanyName] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [companyLogo, setCompanyLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Fetch account settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['account-settings', accountId],
    queryFn: () => accountSettingsService.getSettings(accountId),
    enabled: !!accountId,
  });
  
  // Fetch account details to get registration_slug
  const { data: accountData, isLoading: accountLoading } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountService.getAccount(accountId),
    enabled: !!accountId,
  });
  
  const account = accountData?.data?.attributes;
  const isLoading = settingsLoading || accountLoading;
  
  // Update local state when settings are loaded
  useEffect(() => {
    if (settings) {
      setCompanyName(settings.company_name || '');
      if (settings.company_logo_url) {
        setLogoPreview(settings.company_logo_url);
      }
    }
  }, [settings]);
  
  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (data: { companyName: string; logoKey?: string }) => {
      const updates: Partial<AccountSettings> = { company_name: data.companyName };
      if (data.logoKey) {
        // First confirm the logo upload
        await accountSettingsService.confirmLogoUpload(accountId, data.logoKey);
      }
      return accountSettingsService.updateSettings(accountId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] });
      toast.success('Settings saved successfully!');
    },
    onError: (error) => {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings. Please try again.');
    }
  });
  
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // Validate file locally first
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
          toast.error('Invalid file type. Please upload a JPEG, PNG, GIF, or SVG image.');
          return;
        }
        
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          toast.error('File too large. Maximum size is 5MB.');
          return;
        }
        
        setCompanyLogo(file);
        // Generate preview
        const preview = await logoUploadService.generatePreview(file);
        setLogoPreview(preview);
      } catch (error) {
        console.error('Error processing logo:', error);
        toast.error('Failed to process logo file');
      }
    }
  };
  
  const handleDeleteLogo = async () => {
    try {
      setIsUploading(true);
      await logoUploadService.deleteLogo(accountId);
      setLogoPreview(null);
      setCompanyLogo(null);
      setShowDeleteModal(false);
      
      // Refresh settings
      await queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] });
      toast.success('Company logo deleted successfully');
      setIsUploading(false);
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Failed to delete logo');
      setIsUploading(false);
    }
  };
  
  const handleSaveChanges = async () => {
    // Validate company name
    if (!companyName.trim()) {
      setCompanyNameError('Company name is required');
      return;
    }
    setCompanyNameError('');
    
    try {
      setIsUploading(true);
      
      // Upload logo if there's a new one
      if (companyLogo) {
        try {
          const logoResponse = await logoUploadService.uploadLogo(accountId, companyLogo);
          setLogoPreview(logoResponse.logo_url);
          setCompanyLogo(null);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to upload logo';
          toast.error(errorMessage);
          setIsUploading(false);
          return;
        }
      }
      
      // Update other settings (company name, etc.)
      await updateSettingsMutation.mutateAsync({ companyName });
      
      setIsUploading(false);
    } catch (error) {
      console.error('Error saving changes:', error);
      setIsUploading(false);
    }
  };
  
  // Generate registration link based on registration_slug or slugified name
  const getRegistrationLink = () => {
    const envPortalUrl = import.meta.env.VITE_PORTAL_URL;
    // Ensure base URL ends with a slash
    const portalBaseUrl = envPortalUrl 
      ? (envPortalUrl.endsWith('/') ? envPortalUrl : `${envPortalUrl}/`)
      : `${window.location.origin}/`;

    // Use registration_slug if available
    if (account?.registration_slug) {
      return `${portalBaseUrl}register/${account.registration_slug}`;
    }
    
    // Fallback to slugified account name - this should always produce a result if account.name exists
    if (account?.name) {
      const slugifiedName = account.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      if (slugifiedName) {
        return `${portalBaseUrl}register/${slugifiedName}`;
      }
    }
    
    // No valid link can be generated - return empty string
    return '';
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="medium" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Field>
              <FieldLabel htmlFor="company-name" required>Company Name</FieldLabel>
              <Input
                id="company-name"
                type="text"
                value={companyName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCompanyName(e.target.value);
                  if (companyNameError) setCompanyNameError('');
                }}
                className="w-full max-w-md"
                required
              />
              {companyNameError && <FieldError>{companyNameError}</FieldError>}
            </Field>
            
            <Field>
              <FieldLabel htmlFor="company-logo">Company Logo</FieldLabel>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="h-10 max-w-48 bg-white px-4 py-2 rounded-full border flex items-center justify-center">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Company logo" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <Upload className="h-6 w-6 text-gray-400" />
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload />
                      Upload Company Logo
                    </Button>
                    {logoPreview && (
                      <Button
                        onClick={() => setShowDeleteModal(true)}
                        disabled={isUploading}
                        variant="destructive-outline"
                      >
                        <Trash2 />
                        Delete
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Files accepted jpg, png, or svg. Max size 5MB.</p>
                </div>
              </div>
            </Field>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button
            onClick={handleSaveChanges}
            disabled={isUploading || updateSettingsMutation.isPending}
          >
            {(isUploading || updateSettingsMutation.isPending) && (
              <Spinner size="small" color="light" />
            )}
            Save Changes
          </Button>
        </CardFooter>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Organization Registration Link</CardTitle>
          <CardDescription>
            Share this unique link with your users to allow them to self-register for your organization's portal access.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Field>
            <FieldLabel htmlFor="registration-link">Registration Link</FieldLabel>
            <div className="flex items-center gap-2 max-w-2xl">
              <Input
                id="registration-link"
                type="text"
                value={getRegistrationLink()}
                readOnly
                className="flex-1 bg-gray-50"
              />
              <Button
                onClick={async () => {
                  const link = getRegistrationLink();
                  try {
                    await navigator.clipboard.writeText(link);
                    toast.success('Registration link copied to clipboard!');
                  } catch {
                    toast.error('Failed to copy link');
                  }
                }}
                variant="outline"
              >
                <Copy />
                Copy
              </Button>
            </div>
          </Field>
        </CardContent>
      </Card>
      
      {/* Delete Logo Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Company Logo</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the company logo? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowDeleteModal(false)}
              variant="outline"
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteLogo}
              variant="destructive"
              disabled={isUploading}
            >
              {isUploading && <Spinner size="small" color="light" className="mr-2" />}
              Delete Logo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SecurityTab() {
  const { data: currentUser } = useCurrentUser();
  const accountId = currentUser?.organization_id || '';

  const [usesSamlLogin, setUsesSamlLogin] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch account settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', accountId],
    queryFn: () => accountSettingsService.getSettings(accountId),
    enabled: !!accountId,
  });

  // Update local state when settings are loaded
  useEffect(() => {
    if (settings) {
      setUsesSamlLogin(settings.uses_saml_login ?? false);
    }
  }, [settings]);
  
  const handleExportAuditLog = async () => {
    try {
      setIsExporting(true);
      const blob = await accountSettingsService.exportAuditLog(accountId);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export audit log:', error);
      toast.error('Failed to export audit log. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="medium" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Global Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Global Security Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Switch
                  id="sso-switch"
                  checked={usesSamlLogin}
                  disabled
                  showLabels
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="sso-switch">Single sign on</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  To update your single sign on configurations, please reach out to{' '}
                  <a href="mailto:support@prosci.com" className="text-primary hover:underline">
                    support@prosci.com
                  </a>
                </p>
              </div>
            </div>

            <hr className="my-4" />

            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium">Audit Log Export</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Our audit log exports a CSV of the past 30 days of activity in your account.
                </p>
              </div>
              <Button
                onClick={handleExportAuditLog}
                disabled={isExporting}
                variant="outline"
                className="ml-4"
              >
                {isExporting && <Spinner size="small" />}
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* App Specific Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle>App Specific Security Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground text-base">Coming Soon</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BillingUsageTab() {
  const { data: currentUser } = useCurrentUser();
  const accountId = currentUser?.organization_id || '';
  
  console.log('Current user:', currentUser);
  console.log('Account ID being used:', accountId);
  
  // Fetch account usage data
  const { data: usage, isLoading, error } = useQuery({
    queryKey: ['account-usage', accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('No account ID available');
        return null;
      }
      
      const result = await accountSettingsService.getAccountUsage(accountId);
      console.log('Account usage response:', result);
      return result;
    },
    enabled: !!accountId,
  });
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-[20px] border border-[#e3e6eb] p-6">
        <div className="flex items-center justify-center py-8">
          <Spinner size="medium" />
        </div>
      </div>
    );
  }
  
  if (error) {
    console.error('Error fetching usage data:', error);
  }
  
  const licenses = usage?.licenses || [];
  
  return (
    <div className="space-y-8">
      {/* Licenses */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Licenses</h2>
        {!accountId && (
          <Empty>
            <EmptyIcon>
              <AlertTriangle />
            </EmptyIcon>
            <EmptyTitle>No organization ID found</EmptyTitle>
            <EmptyDescription>
              Please ensure you are logged in as a license admin.
            </EmptyDescription>
          </Empty>
        )}
        
        {licenses.length > 0 ? (
          <div className="grid gap-4">
            {licenses.map((license) => {
              const isOverLimit = license.used_seats > license.total_seats;
              const usagePercentage = (license.used_seats / license.total_seats) * 100;
              const isExpired = license.expiration_date && new Date(license.expiration_date) < new Date();
              
              return (
                <Card key={license.id} size="medium">
                  <CardContent>
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-4">
                        <div>
                          <h3 className="text-lg font-semibold">{license.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {license.expiration_date ? (
                              <>
                                {isExpired ? 'Expired' : 'Expiration'} {new Date(license.expiration_date).toLocaleDateString('en-US', { 
                                  month: 'long', 
                                  day: 'numeric',
                                  year: 'numeric' 
                                })}
                              </>
                            ) : (
                              'Ongoing'
                            )}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost"
                            size="icon"
                            aria-label={`More options for ${license.name}`}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem asChild>
                            <a href="mailto:support@prosci.com">
                              Contact Support
                            </a>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-[16px] font-medium">
                          {license.used_seats} / {license.total_seats} Seats Used
                        </span>
                        {isOverLimit && (
                          <span className="text-[14px] italic">
                            {license.used_seats - license.total_seats} Seats Over
                          </span>
                        )}
                      </div>
                      <div className="relative w-full">
                        <Progress 
                          value={usagePercentage} 
                          max={100}
                    
                          size="md"
                        />
                      </div>
                    </div>
                  </CardContent>
                    {isOverLimit && (
                      <CardFooter className="bg-destructive-50 py-4">
                        <p className="text-destructive font-semibold text-center w-full">
                          Contact sales to add more seats to your plan
                        </p>
                      </CardFooter>
                    )}

                </Card>
              );
            })}
          </div>
        ) : (
          <Empty>
            <EmptyIcon>
              <CreditCard />
            </EmptyIcon>
            <EmptyTitle>No active licenses found</EmptyTitle>
            <EmptyDescription>
              Please contact support to add a license to your account.
            </EmptyDescription>
          </Empty>
        )}
      </div>
      
    </div>
  );
}

function AppLevelSettingsTab() {
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const accountId = currentUser?.organization_id || '';

  // This tab should only be visible to license admins
  if (currentUser?.type !== 'license_admin') {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <p className="text-muted-foreground">App Level Settings are only available for License Admins.</p>
        </div>
      </div>
    );
  }

  const [appSettings, setAppSettings] = useState<AppSettings | null>(null);
  const [confirmState, setConfirmState] = useState<{
    settingKey: keyof AppSettings;
    nextValue: boolean;
  } | null>(null);

  // Feature-level settings state
  const [featureConfirmState, setFeatureConfirmState] = useState<{
    featureCode: string;
    nextValue: boolean;
    mode?: FeatureMode;
  } | null>(null);

  const { data: settings, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ['account-app-settings', accountId],
    queryFn: () => accountSettingsService.getAppSettings(accountId),
    enabled: !!accountId && !userLoading,
  });

  const { data: affectedUsers, isLoading: affectedUsersLoading, error: affectedUsersError } = useQuery({
    queryKey: ['account-app-settings-affected-users', accountId],
    queryFn: () => accountSettingsService.getAppSettingsAffectedUsers(accountId),
    enabled: !!accountId && !userLoading,
  });

  const { data: accountSettings, isLoading: accountSettingsLoading } = useQuery({
    queryKey: ['account-settings', accountId],
    queryFn: () => accountSettingsService.getSettings(accountId),
    enabled: !!accountId && !userLoading,
  });

  const { data: publicLinksCount } = useQuery({
    queryKey: ['account-public-links-count', accountId],
    queryFn: () => accountSettingsService.getPublicLinksCount(accountId),
    enabled: !!accountId && !userLoading,
  });

  const proximaPublicLinksEnabled = accountSettings?.enabled_features?.some(
    (f) => f.code === 'proxima_public_links'
  ) ?? true;

  const kaiyaFileUploadEnabled = accountSettings?.enabled_features?.some(
    (f) => f.code === 'file_upload'
  ) ?? true;

  const kaiyaPublicLinksEnabled = accountSettings?.kaiya_public_links_mode === 'all_users';

  // Memory availability is a mode, not a plain enable: 'per_user' makes the
  // opt-in available to users (never forced on), 'disabled' blocks it org-wide.
  // An org that has never set a mode has not made memory available, so the
  // toggle reads off until a License Admin turns it on.
  const kaiyaMemoryEnabled =
    accountSettings?.kaiya_memory_mode != null && accountSettings.kaiya_memory_mode !== 'disabled';

  const kaiyaMemoryAvailable = accountSettings?.kaiya_memory_available === true;

  const guidedUseCasesAvailable = accountSettings?.guided_use_cases_available === true;
  const guidedUseCasesMode: FeatureMode = accountSettings?.guided_use_cases_mode ?? 'disabled';
  const guidedUseCasesEnabled = guidedUseCasesMode !== 'disabled';

  const enableFeatureMutation = useMutation({
    mutationFn: async (featureCode: string) => {
      return accountSettingsService.enableFeature(accountId, featureCode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] });
      toast.success('Feature setting updated successfully.');
    },
    onError: (error) => {
      console.error('Failed to enable feature:', error);
      toast.error('Failed to update feature setting. Please try again.');
    },
  });

  const disableFeatureMutation = useMutation({
    mutationFn: async (featureCode: string) => {
      await accountSettingsService.disableFeature(accountId, featureCode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] });
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('Feature setting updated successfully.');
    },
    onError: (error) => {
      console.error('Failed to disable feature:', error);
      toast.error('Failed to update feature setting. Please try again.');
    },
  });

  const setFeatureModeMutation = useMutation({
    mutationFn: async ({ featureCode, mode }: { featureCode: string; mode: FeatureMode }) => {
      await accountSettingsService.setFeatureMode(accountId, featureCode, mode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] });
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('Feature setting updated successfully.');
    },
    onError: (error) => {
      console.error('Failed to set feature mode:', error);
      toast.error('Failed to update feature setting. Please try again.');
    },
  });

  useEffect(() => {
    if (settings) {
      setAppSettings(settings);
    }
  }, [settings]);

  const updateAppSettingsMutation = useMutation({
    mutationFn: async (updates: Partial<AppSettings>) => {
      return accountSettingsService.updateAppSettings(accountId, updates);
    },
    onSuccess: (updatedSettings) => {
      setAppSettings(updatedSettings);
      queryClient.invalidateQueries({ queryKey: ['account-app-settings', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-app-settings-affected-users', accountId] });
      toast.success('App level settings updated.');
    },
    onError: (error) => {
      console.error('Failed to update app settings:', error);
      toast.error('Failed to update app level settings. Please try again.');
    }
  });

  const membershipProducts: Array<{
    settingKey: keyof AppSettings;
    name: string;
    description: string;
  }> = [
    {
      settingKey: 'community_enabled',
      name: 'Community',
      description: 'Enable Community for discussions, resources, and peer connection.'
    },
    {
      settingKey: 'kaiya_enabled',
      name: 'Kaiya',
      description: 'Enable Kaiya for AI-powered change management guidance and support.'
    },
    {
      settingKey: 'pathways_enabled',
      name: 'Pathways',
      description: 'Enable Pathways for guided learning and application experience.'
    }
  ];

  const isLoading = userLoading || settingsLoading || affectedUsersLoading || accountSettingsLoading;

  const handleToggleChange = (settingKey: keyof AppSettings, nextValue: boolean) => {
    if (!appSettings) return;

    if (!nextValue) {
      setConfirmState({ settingKey, nextValue });
      return;
    }

    setAppSettings({ ...appSettings, [settingKey]: nextValue });
    updateAppSettingsMutation.mutate({ [settingKey]: nextValue });
  };

  const handleConfirmDisable = () => {
    if (!confirmState || !appSettings) return;

    const { settingKey, nextValue } = confirmState;
    setConfirmState(null);
    setAppSettings({ ...appSettings, [settingKey]: nextValue });
    updateAppSettingsMutation.mutate({ [settingKey]: nextValue });
  };

  const handleFeatureToggleChange = (featureCode: string, nextValue: boolean) => {
    setFeatureConfirmState({ featureCode, nextValue });
  };

  const handleConfirmFeatureToggle = () => {
    if (!featureConfirmState) return;

    const { featureCode, nextValue, mode } = featureConfirmState;
    setFeatureConfirmState(null);

    if (mode) {
      setFeatureModeMutation.mutate({ featureCode, mode });
      return;
    }

    if (featureCode === 'kaiya_memory') {
      setFeatureModeMutation.mutate({ featureCode, mode: nextValue ? 'per_user' : 'disabled' });
      return;
    }

    if (featureCode === 'guided_use_cases') {
      setFeatureModeMutation.mutate({ featureCode, mode: nextValue ? 'all_users' : 'disabled' });
      return;
    }

    if (nextValue) {
      enableFeatureMutation.mutate(featureCode);
    } else {
      disableFeatureMutation.mutate(featureCode);
    }
  };

  if (settingsError || affectedUsersError) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load app settings</p>
          <p className="text-sm text-muted-foreground">
            {settingsError instanceof Error ? settingsError.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || !appSettings) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="medium" />
      </div>
    );
  }

  if (!accountId) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <p className="text-muted-foreground">No organization ID found. Please ensure you are logged in as a license admin.</p>
        </div>
      </div>
    );
  }

  const confirmProduct = membershipProducts.find((p) => p.settingKey === confirmState?.settingKey);
  const affectedCount = confirmState && affectedUsers ? (affectedUsers[confirmState.settingKey] ?? 0) : 0;

  return (
    <div className="space-y-8">
      {/* Membership Settings */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl">Membership Settings</CardTitle>
              <CardDescription className="mt-1.5">
                Control product access for your organization members. Disable products to restrict data entry capabilities.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y divide-gray-200">
            {membershipProducts.map((product) => (
              <div
                key={product.settingKey}
                className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              >
                <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex items-start gap-3 sm:flex-1">
                    <div className="mt-0.5 shrink-0">
                      <Switch
                        id={`app-setting-${product.settingKey}`}
                        className="cursor-pointer"
                        checked={appSettings[product.settingKey]}
                        onCheckedChange={(value: boolean) => handleToggleChange(product.settingKey, value)}
                        showLabels
                        disabled={updateAppSettingsMutation.isPending}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Label htmlFor={`app-setting-${product.settingKey}`} className="text-base font-medium">
                        {product.name}
                      </Label>
                      <p className="text-sm text-muted-foreground mt-0.5">{product.description}</p>
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground shrink-0 sm:ml-auto">All Access</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Kaiya Settings */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Lightbulb className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl">Kaiya Settings</CardTitle>
              <CardDescription className="mt-1.5">
                Manage Kaiya preferences for your organization.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y divide-gray-200">
            <div className="flex flex-col gap-2 py-4 first:pt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex items-start gap-3 sm:flex-1">
                <div className="mt-0.5 shrink-0">
                  <Switch
                    id="kaiya-file-upload"
                    className="cursor-pointer"
                    checked={kaiyaFileUploadEnabled}
                    onCheckedChange={(value: boolean) => handleFeatureToggleChange('file_upload', value)}
                    showLabels
                    disabled={enableFeatureMutation.isPending || disableFeatureMutation.isPending}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <Label htmlFor="kaiya-file-upload" className="text-base font-medium">
                    File Upload
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Allow users to upload documents to Kaiya for AI-powered analysis and contextual responses.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex items-start gap-3 sm:flex-1">
                <div className="mt-0.5 shrink-0">
                  <Switch
                    id="kaiya-public-links"
                    className="cursor-pointer"
                    checked={kaiyaPublicLinksEnabled}
                    onCheckedChange={(value: boolean) => handleFeatureToggleChange('kaiya_public_links', value)}
                    showLabels
                    disabled={enableFeatureMutation.isPending || disableFeatureMutation.isPending}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <Label htmlFor="kaiya-public-links" className="text-base font-medium">
                    Public Links
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Allow users to share a Kaiya canvas with anyone holding the link, without signing in. Canvases
                    are always shareable inside your workspace; this only adds the public option.
                  </p>
                </div>
              </div>
            </div>
            {kaiyaMemoryAvailable && (
              <div className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div className="flex items-start gap-3 sm:flex-1">
                  <div className="mt-0.5 shrink-0">
                    <Switch
                      id="kaiya-memory"
                      className="cursor-pointer"
                      checked={kaiyaMemoryEnabled}
                      onCheckedChange={(value: boolean) => handleFeatureToggleChange('kaiya_memory', value)}
                      showLabels
                      disabled={setFeatureModeMutation.isPending}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Label htmlFor="kaiya-memory" className="text-base font-medium">
                      Memory
                    </Label>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Make Kaiya memory available to your users. Memory is always off until a user individually
                      opts in — enabling this only lets users choose it for themselves.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {guidedUseCasesAvailable && (
              <div className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="flex items-start gap-3 sm:flex-1">
                  <div className="mt-0.5 shrink-0">
                    <Switch
                      id="guided-use-cases"
                      className="cursor-pointer"
                      checked={guidedUseCasesEnabled}
                      onCheckedChange={(value: boolean) => handleFeatureToggleChange('guided_use_cases', value)}
                      showLabels
                      disabled={setFeatureModeMutation.isPending}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Label htmlFor="guided-use-cases" className="text-base font-medium">
                      Guided Use Cases
                    </Label>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Give users coached, artifact-building canvases inside Kaiya — ADKAR Blueprint, ADKAR
                      Outcomes, Activate a Role and the rest.
                    </p>
                    {guidedUseCasesEnabled && (
                      <div className="mt-4 max-w-md">
                        <span className="text-sm font-medium">Who gets guided use cases:</span>
                        <RadioGroup
                          value={guidedUseCasesMode}
                          onValueChange={(value: string) => {
                            if (value === 'all_users') {
                              setFeatureConfirmState({ featureCode: 'guided_use_cases', nextValue: true, mode: 'all_users' });
                              return;
                            }
                            setFeatureModeMutation.mutate({ featureCode: 'guided_use_cases', mode: value as FeatureMode });
                          }}
                          disabled={setFeatureModeMutation.isPending}
                          className="mt-2"
                        >
                          <label
                            htmlFor="guided-use-cases-all"
                            className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                          >
                            <RadioGroupItem value="all_users" id="guided-use-cases-all" className="mt-0.5" />
                            <div>
                              <span className="text-sm font-medium">Everyone in the organization</span>
                              <p className="text-sm text-muted-foreground">
                                Every user on your Kaiya licence gets guided use cases, with no per-user setup.
                              </p>
                            </div>
                          </label>
                          <label
                            htmlFor="guided-use-cases-per-user"
                            className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                          >
                            <RadioGroupItem value="per_user" id="guided-use-cases-per-user" className="mt-0.5" />
                            <div>
                              <span className="text-sm font-medium">Only users I select</span>
                              <p className="text-sm text-muted-foreground">
                                Grant guided use cases person by person in Users → Edit User → Apps → Kaiya.
                              </p>
                            </div>
                          </label>
                        </RadioGroup>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Proxima Settings */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Settings className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl">Proxima Settings</CardTitle>
              <CardDescription className="mt-1.5">
                Manage Proxima preferences for your organization.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y divide-gray-200">
            <div className="flex flex-col gap-2 py-4 first:pt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex items-start gap-3 sm:flex-1">
                <div className="mt-0.5 shrink-0">
                  <Switch
                    id="proxima-public-links"
                    className="cursor-pointer"
                    checked={proximaPublicLinksEnabled}
                    onCheckedChange={(value: boolean) => handleFeatureToggleChange('proxima_public_links', value)}
                    showLabels
                    disabled={enableFeatureMutation.isPending || disableFeatureMutation.isPending}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <Label htmlFor="proxima-public-links" className="text-base font-medium">
                    Public Links
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Allow users to create and share read-only public links to Proxima projects.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!confirmState} onOpenChange={() => setConfirmState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable {confirmProduct?.name}</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Clicking <strong>Confirm</strong> will disable {confirmProduct?.name} for {affectedCount} users.
                  Active sessions will remain open, but users will lose access the next time they log in.{' '}
                  <a
                    href="https://help.prosci.com/en/articles/13553276-license-admin-managing-membership-product-settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Learn more about Membership Product Settings
                  </a>
                  .
                </p>
                {confirmState?.settingKey === 'community_enabled' && (
                  <p className="text-destructive font-medium">
                    Warning: This will permanently remove all users from the community platform. Users will need to be
                    re-invited and re-onboarded if Community access is restored.
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setConfirmState(null)} variant="outline" disabled={updateAppSettingsMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDisable} variant="destructive" disabled={updateAppSettingsMutation.isPending}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!featureConfirmState} onOpenChange={() => setFeatureConfirmState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {featureConfirmState?.featureCode === 'proxima_public_links'
                ? `${featureConfirmState.nextValue ? 'Enable' : 'Disable'} Proxima Public Links`
                : featureConfirmState?.featureCode === 'kaiya_public_links'
                ? `${featureConfirmState.nextValue ? 'Enable' : 'Disable'} Kaiya Public Links`
                : featureConfirmState?.featureCode === 'kaiya_memory'
                ? `${featureConfirmState.nextValue ? 'Enable' : 'Disable'} Kaiya Memory`
                : featureConfirmState?.mode === 'all_users'
                ? 'Enrol everyone in Guided Use Cases'
                : featureConfirmState?.featureCode === 'guided_use_cases'
                  ? `${featureConfirmState.nextValue ? 'Enable' : 'Disable'} Guided Use Cases`
                  : `${featureConfirmState?.nextValue ? 'Enable' : 'Disable'} Kaiya File Upload`}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3">
                {featureConfirmState?.featureCode === 'proxima_public_links' ? (
                  featureConfirmState.nextValue ? (
                    <p>
                      Enabling Proxima Public Links will allow all users to create and share
                      read-only public links to Proxima projects. For more information, please refer to the help center.
                    </p>
                  ) : (
                    <p>
                      Clicking Confirm will disable Public Links for Proxima, preventing users from
                      creating new public links to projects.
                      {publicLinksCount != null && publicLinksCount > 0 && (
                        <> This will deactivate {publicLinksCount} existing public {publicLinksCount === 1 ? 'link' : 'links'}.</>
                      )}{' '}
                      <a href="https://help.prosci.com/en/collections/18392245-license-admin-product-settings" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Learn more about License Admin Product Settings
                      </a>
                    </p>
                  )
                ) : featureConfirmState?.featureCode === 'kaiya_public_links' ? (
                  featureConfirmState.nextValue ? (
                    <p>
                      Enabling Kaiya Public Links lets your users create a canvas link that opens for anyone who
                      has it, without signing in. A canvas is only ever public if the user chooses that option —
                      links stay workspace-only by default.
                    </p>
                  ) : (
                    <p>
                      Clicking Confirm will stop users creating new public canvas links, and any existing public
                      link will stop opening for people outside your organization.{' '}
                      <a href="https://help.prosci.com/en/collections/18392245-license-admin-product-settings" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Learn more about License Admin Product Settings
                      </a>
                    </p>
                  )
                ) : featureConfirmState?.featureCode === 'kaiya_memory' ? (
                  featureConfirmState.nextValue ? (
                    <p>
                      Enabling Kaiya Memory makes the feature available to your organization's users. Memory stays
                      off for every user until they individually opt in from their own settings — it is never turned
                      on for anyone automatically.
                    </p>
                  ) : (
                    <p>
                      Clicking Confirm will disable Kaiya Memory for your organization. Kaiya will stop remembering
                      and recalling information for all users, including those who previously opted in, and the
                      memory settings section will no longer appear for them. Stored memories are retained — nothing
                      is deleted. If you re-enable Kaiya Memory later, users who had turned it on will need to opt in
                      again from their settings before Kaiya resumes remembering.
                    </p>
                  )
                ) : featureConfirmState?.mode === 'all_users' ? (
                  <p>
                    Clicking Confirm gives every user in your organization guided use cases, and clears the access
                    you granted to individual people. Moving back to selected users afterwards starts from nobody.
                  </p>
                ) : featureConfirmState?.featureCode === 'guided_use_cases' ? (
                  featureConfirmState.nextValue ? (
                    <p>
                      Enabling Guided Use Cases turns them on for everyone in your organization, which clears any
                      access you had granted to individual people. You can narrow it back to selected users
                      afterwards, but those individual grants will need setting up again.
                    </p>
                  ) : (
                    <p>
                      Clicking Confirm will disable Guided Use Cases for your organization. Users lose the guided
                      canvases in Kaiya, including anyone you granted access to individually. Work already saved on
                      a canvas is retained — nothing is deleted.
                    </p>
                  )
                ) : featureConfirmState?.nextValue ? (
                  <p>
                    Enabling Kaiya File Upload will allow all users to upload files into Kaiya
                    conversations. For more information, please refer to the help center.
                  </p>
                ) : (
                  <p>
                    Clicking Confirm will disable the File Upload feature in Kaiya for {affectedUsers?.kaiya_enabled ?? 0} users,
                    removing the ability to upload files into Kaiya conversations. Disabling this will not remove context or delete any files
                    from existing Kaiya conversations.{' '}
                    <a href="https://help.prosci.com/en/collections/18392245-license-admin-product-settings" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Learn more about License Admin Product Settings
                    </a>
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setFeatureConfirmState(null)}
              variant="outline"
              disabled={enableFeatureMutation.isPending || disableFeatureMutation.isPending || setFeatureModeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmFeatureToggle}
              variant={featureConfirmState?.nextValue ? 'default' : 'destructive'}
              disabled={enableFeatureMutation.isPending || disableFeatureMutation.isPending || setFeatureModeMutation.isPending}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}